
import { API } from "@/lib/api";
import { getStartEndOfDateRangeInterval } from "@/lib/seller-analytics";
import { DateRangeValue } from "@/utils/dateUtils";
import {
  ProductSalesResponseBody,
  SalesBreakdownResponseBody,
  SalesSnapShotInterval,
  SalesSnapshotRequestBody,
  SalesSnapshotResponseBody,
  SkuSalesResponseBody,
  ProfitabilityByProductOverviewResponseBody,
  ProfitabilityByProductResponseBody,
  ProfitabilityByProductBreakdown,
  CategoryProfitAnalysisResponse, ProfitabilityByCategoryOverviewResponseBody,
  ProfitabilityByAsinResponseBody, ProfitabilityByAsinOverviewResponseBody, ProfitabilityByAsinBreakdown,
  OrderItemSalesResponseBody, ProfitabilityByCategoryBreakdown,
  ProfitabilityByCategoryProductsSold,
  ProfitAndLoss,
} from "@boxem/core-api-schema";

interface ProductSalesBreakdownOptions {
  interval: DateRangeValue;
}

interface SkuSalesSnapshotOptions {
  interval: DateRangeValue;
  sku: string;
}

export async function getSellerSalesSnapshot(dateRangeIntervals: DateRangeValue[]): Promise<SalesSnapshotResponseBody> {
  const snaphotIntervals: SalesSnapShotInterval[] = dateRangeIntervals.map(dateRange => getStartEndOfDateRangeInterval(dateRange.from, dateRange.to));

  const requestBody: SalesSnapshotRequestBody = {
    intervals: snaphotIntervals
  }

  return API.post<SalesSnapshotResponseBody>('/seller-analytics/sales-snapshot', requestBody, null);
}

export async function getSellerSalesBreakdown(dateRangeInterval: DateRangeValue): Promise<SalesBreakdownResponseBody> {
  const requestBody = getStartEndOfDateRangeInterval(dateRangeInterval.from, dateRangeInterval.to);
  return API.post<SalesBreakdownResponseBody>(`/seller-analytics/sales-breakdown`, requestBody, null);
}

export async function getProductSalesBreakdown(dateRangeInterval: DateRangeValue): Promise<ProductSalesResponseBody> {
  const { from, to } = getStartEndOfDateRangeInterval(dateRangeInterval.from, dateRangeInterval.to);
  return API.get<ProductSalesResponseBody>('/seller-analytics/product-sales', new URLSearchParams({
    from: from.toString(),
    to: to.toString(),
  }));
}
export async function getSkuSalesBreakdown({ interval, sku }: SkuSalesSnapshotOptions): Promise<SkuSalesResponseBody> {
  const { from, to } = getStartEndOfDateRangeInterval(interval.from, interval.to);
  return API.get<SkuSalesResponseBody>(`/seller-analytics/product-sales/${sku}`, new URLSearchParams({
    from: from.toString(),
    to: to.toString(),
  }));
}

export async function getOrderItemSalesBreakdown(dateRangeInterval: DateRangeValue): Promise<OrderItemSalesResponseBody> {
  const { from, to } = getStartEndOfDateRangeInterval(dateRangeInterval.from, dateRangeInterval.to);
  return API.get<OrderItemSalesResponseBody>('/seller-analytics/order-item-sales', new URLSearchParams({
    from: from.toString(),
    to: to.toString(),
  }));
}

export async function getSkuProfitabilityBreakdown(dateRangeInterval: DateRangeValue): Promise<ProfitabilityByProductOverviewResponseBody> {
  const { from, to } = getStartEndOfDateRangeInterval(dateRangeInterval.from, dateRangeInterval.to);
  return API.get<ProfitabilityByProductOverviewResponseBody>(`/seller-analytics/profit-by-product/overview`, new URLSearchParams({
    from: from.toString(),
    to: to.toString(),
  }));
}

export async function getSkuProfitability({ interval, page = 1, limit = 10, search = '' }): Promise<ProfitabilityByProductResponseBody> {
  const { from, to } = getStartEndOfDateRangeInterval(interval.from, interval.to);

  return API.get<ProfitabilityByProductResponseBody>('/seller-analytics/profit-by-product', new URLSearchParams({
    from: from.toString(),
    to: to.toString(),
    page: page.toString(),
    limit: limit.toString(),
    ...(search ? { search } : {}),
  }));
}

export async function getSkuProfitabilityBySku({ sku, interval }): Promise<ProfitabilityByProductBreakdown> {
  const { from, to } = getStartEndOfDateRangeInterval(interval.from, interval.to);
  return API.get<ProfitabilityByProductBreakdown>(`/seller-analytics/profit-by-product/${sku}`, new URLSearchParams({
    from: from.toString(),
    to: to.toString(),
  }));
}

export async function getAsinProfitability({ interval, page = 1, limit = 10, search = '' }): Promise<ProfitabilityByAsinResponseBody> {
  const { from, to } = getStartEndOfDateRangeInterval(interval.from, interval.to);
  return API.get<ProfitabilityByAsinResponseBody>(`/seller-analytics/profit-by-asin`, new URLSearchParams({
    from: from.toString(),
    to: to.toString(),
    page: page.toString(),
    limit: limit.toString(),
    ...(search ? { search } : {}),
  }));
}

export async function getAsinProfitabilityBreakDown({ interval }): Promise<ProfitabilityByAsinOverviewResponseBody> {
  const { from, to } = getStartEndOfDateRangeInterval(interval.from, interval.to);
  return API.get<ProfitabilityByAsinOverviewResponseBody>(`/seller-analytics/profit-by-asin/overview`, new URLSearchParams({
    from: from.toString(),
    to: to.toString(),
  }));
}

export async function getAsinProfitabilityByAsin({ asin, interval }) {
  const { from, to } = getStartEndOfDateRangeInterval(interval.from, interval.to);
  return API.get<ProfitabilityByAsinBreakdown>(`/seller-analytics/profit-by-asin/${asin}`, new URLSearchParams({
    from: from.toString(),
    to: to.toString(),
  }));
}

export async function getCategoryProfitability({ interval, page = 1, limit = 10, search = '' }): Promise<CategoryProfitAnalysisResponse> {
  const { from, to } = getStartEndOfDateRangeInterval(interval.from, interval.to);
  return API.get<CategoryProfitAnalysisResponse>(`/seller-analytics/profit-by-category`, new URLSearchParams({
    from: from.toString(),
    to: to.toString(),
    page: page.toString(),
    limit: limit.toString(),
    ...(search ? { search } : {}),
  }));
}

export async function getCategoryProfitabilityBreakDown({ interval }): Promise<ProfitabilityByCategoryOverviewResponseBody> {
  const { from, to } = getStartEndOfDateRangeInterval(interval.from, interval.to);
  return API.get<ProfitabilityByCategoryOverviewResponseBody>(`/seller-analytics/profit-by-category/overview`, new URLSearchParams({
    from: from.toString(),
    to: to.toString(),
  }));
}

export async function getCategoryProfitabilityBreakDownByCategory({ interval, category }): Promise<ProfitabilityByCategoryBreakdown> {
  const { from, to } = getStartEndOfDateRangeInterval(interval.from, interval.to);
  return API.get<ProfitabilityByCategoryBreakdown>(`/seller-analytics/profit-by-category/${encodeURIComponent(category)}`, new URLSearchParams({
    from: from.toString(),
    to: to.toString()
  }));
}

export async function getCategoryProfitabilityProductsSold({ interval, category }): Promise<ProfitabilityByCategoryProductsSold> {
  const { from, to } = getStartEndOfDateRangeInterval(interval.from, interval.to);
  return API.get<ProfitabilityByCategoryProductsSold>(`/seller-analytics/profit-by-category/products-sold/${encodeURIComponent(category)}`, new URLSearchParams({
    from: from.toString(),
    to: to.toString()
  }));
}

export async function getProfitAndLossComparison({ firstInterval, secondInterval }): Promise<ProfitAndLoss> {
  const { from:  firstFrom, to: firstTo } = getStartEndOfDateRangeInterval(firstInterval.from, firstInterval.to);
  const { from: secondFrom, to: secondTo } = getStartEndOfDateRangeInterval(secondInterval.from, secondInterval.to);
  return API.get<ProfitAndLoss>('/seller-analytics/profit-loss', new URLSearchParams({
    firstFrom: firstFrom.toString(),
    firstTo: firstTo.toString(),
    secondFrom: secondFrom.toString(),
    secondTo: secondTo.toString(),
  }))
}

